import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, Users, Wrench } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  ProblemIdentificationStep,
  ClassroomEnvironmentStep,
  TeachingApproachStep,
} from "./classroom-management/steps";
import ClassroomManagementSpecificFields from "./classroom-management/ClassroomManagementSpecificFields";
import {
  ClassroomManagementCreateRequestDto,
  ClassroomManagementCreateRequestDtoContentLanguage,
  ClassroomManagementCreateRequestDtoCourseName,
  ClassroomManagementCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";

interface ClassroomManagementCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string;
}

export const ClassroomManagementCreateModal: React.FC<
  ClassroomManagementCreateModalProps
> = ({ onSubmit, initialInput, ...props }) => {
  const { t } = useTranslation();

  // State for basic problem description field
  const [basicProblemDescription, setBasicProblemDescription] = useState(
    (initialInput as any).problemDescription || "",
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: ClassroomManagementCreateRequestDto = {
    // Basic CreateModal data (will be populated from first step)
    courseName:
      initialInput.courseName as ClassroomManagementCreateRequestDtoCourseName,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType:
      initialInput.generationType as ClassroomManagementCreateRequestDtoGenerationType,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage:
      initialInput.outputLanguage as ClassroomManagementCreateRequestDtoContentLanguage,

    // Required field
    problemDescription: basicProblemDescription,

    // Advanced classroom management specific defaults
    problemCategory: "",
    problemSubcategory: "",
    classroomManagementGoal: "",
    classSize: "",
    teachingToolsUsed: "",
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<ClassroomManagementCreateRequestDto>[] =
    [
      {
        id: "problem-identification",
        title: t("classroomManagement.steps.problemIdentification.title"),
        description: t(
          "classroomManagement.steps.problemIdentification.description",
        ),
        icon: <Target size={16} />,
        component: ProblemIdentificationStep,
        validation: (data) => {
          // Problem category and subcategory are optional, so always valid
          return true;
        },
      },
      {
        id: "classroom-environment",
        title: t("classroomManagement.steps.classroomEnvironment.title"),
        description: t(
          "classroomManagement.steps.classroomEnvironment.description",
        ),
        icon: <Users size={16} />,
        component: ClassroomEnvironmentStep,
        validation: (data) => {
          // Management goal and class size are optional, so always valid
          return true;
        },
      },
      {
        id: "teaching-approach",
        title: t("classroomManagement.steps.teachingApproach.title"),
        description: t(
          "classroomManagement.steps.teachingApproach.description",
        ),
        icon: <Wrench size={16} />,
        component: TeachingApproachStep,
        validation: (data) => {
          // Teaching tools used is optional, so always valid
          return true;
        },
      },
    ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    let submissionData = { ...data };

    // Add problem description if provided
    if (basicProblemDescription.trim()) {
      submissionData.problemDescription = basicProblemDescription.trim();
    }

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (
    data: ClassroomManagementCreateRequestDto,
  ) => {
    let submissionData = { ...data };

    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add problem description if provided
    if (data.problemDescription?.trim()) {
      enrichedPrompt = `Problem Description: ${data.problemDescription.trim()}\n\n${enrichedPrompt}`;
    }

    // Add problem category
    if (data.problemCategory && data.problemCategory.trim()) {
      enrichedPrompt += `\n\nProblem Category: ${data.problemCategory}`;
    }

    // Add problem subcategory
    if (data.problemSubcategory && data.problemSubcategory.trim()) {
      enrichedPrompt += `\nProblem Subcategory: ${data.problemSubcategory}`;
    }

    // Add classroom management goal
    if (data.classroomManagementGoal && data.classroomManagementGoal.trim()) {
      enrichedPrompt += `\nClassroom Management Goal: ${data.classroomManagementGoal}`;
    }

    // Add class size
    if (data.classSize && data.classSize.trim()) {
      enrichedPrompt += `\nClass Size: ${data.classSize}`;
    }

    // Add teaching tools used
    if (data.teachingToolsUsed && data.teachingToolsUsed.trim()) {
      enrichedPrompt += `\nTeaching Tools Used: ${data.teachingToolsUsed}`;
    }

    submissionData.prompt = enrichedPrompt;

    await onSubmit(submissionData);
  };

  // Handle problem description change from basic mode
  const handleProblemDescriptionChange = (problemDescription: string) => {
    setBasicProblemDescription(problemDescription);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<ClassroomManagementCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "fuchsia",
        onSubmit: handleBasicSubmit,
        after: (
          <ClassroomManagementSpecificFields
            problemDescription={basicProblemDescription}
            onChange={handleProblemDescriptionChange}
            themeColor="fuchsia"
          />
        ),
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t("quickActions.classroomManagementCreate"),
        themeColor: "fuchsia",
        backPath: "/content/classroom-management",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate"),
        advancedModeSubmitText: t("classroomManagement.createAdvanced"),
      },
      themeColor: "fuchsia",
      title: t("quickActions.classroomManagementCreate"),
      backPath: "/content/classroom-management",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default ClassroomManagementCreateModal;
